import { useQuery } from '@tanstack/react-query';
import type { PresencaChamada } from '@arena/shared/dominio';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { turmasApi } from '../api/turmas.js';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Avatar, Mini, Status, Vazio, type Situacao } from '../ui/primitivos.jsx';
import { ROTULO_ESTADO_NA_CHAMADA } from './formatos.js';

/**
 * A aba "Alunos da turma" de `#m-evento` — prometida pelo documento 06
 * para quando as turmas chegassem.
 *
 * Quem é da turma NO DIA desta aula, com a presença de cada um no período
 * da matrícula dele e o que foi marcado nesta aula. "Receita da turma", o
 * segundo número do protótipo, é das faturas (documento 12): até lá o
 * lugar dele mostra as vagas livres.
 *
 * O servidor recusa com 403 quem não alcança a turma — o Professor que
 * abriu a aula de outro professor na grade — e a tela diz isso, em vez de
 * mostrar uma turma vazia.
 */

const SITUACAO_DO_ESTADO: Readonly<Record<PresencaChamada, Situacao>> = {
  Presente: 'ok',
  Falta: 'atrasada',
  Reposicao: 'aguardando',
};

export function AlunosDaAula({ marcacaoId }: { marcacaoId: string }) {
  const consulta = useQuery({
    queryKey: ['turmas', 'aula', marcacaoId, 'alunos'],
    queryFn: () => turmasApi.alunosDaAula(marcacaoId),
    retry: false,
  });

  if (consulta.isPending) return <EsqueletoDeCard linhas={4} />;

  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <Erro
        tipo={
          erro instanceof ErroDeRede
            ? 'rede'
            : erro instanceof ErroDeApi && erro.ehPermissao
              ? 'permissao'
              : 'falha'
        }
        detalhe={
          erro instanceof ErroDeApi && erro.ehPermissao
            ? 'Esta aula é de uma turma que não é sua.'
            : mensagemDoErro(erro)
        }
        onTentarDeNovo={() => void consulta.refetch()}
      />
    );
  }

  const { turma, alunos, vagas, presencaMedia } = consulta.data;

  return (
    <>
      <Mini
        itens={[
          { valor: `${turma.matriculados} de ${turma.capacidade}`, rotulo: 'alunos matriculados' },
          { valor: vagas, rotulo: vagas === 1 ? 'vaga livre' : 'vagas livres' },
          {
            valor: presencaMedia === null ? '—' : `${presencaMedia}%`,
            rotulo: 'presença média',
          },
        ]}
      />
      <div>
        <div className="card-head" style={{ marginBottom: 10 }}>
          <h2>Alunos da turma</h2>
          <span className="hint">
            {turma.esperando > 0
              ? `${turma.esperando} na lista de espera`
              : `${vagas} ${vagas === 1 ? 'vaga livre' : 'vagas livres'}`}
          </span>
        </div>
        {alunos.length === 0 ? (
          <Vazio>Ninguém matriculado nesta turma na data desta aula.</Vazio>
        ) : (
          <ul className="feed">
            {alunos.map((a) => (
              <li key={a.matriculaId}>
                <Avatar nome={a.aluno.nome} />
                <div>
                  <b>{a.aluno.nome}</b>
                  <small>
                    {a.faltasSeguidas >= 2
                      ? `${a.faltasSeguidas} faltas seguidas`
                      : a.presenca === null
                        ? 'sem chamada registrada ainda'
                        : `presença de ${a.presenca}%`}
                  </small>
                </div>
                <div className="rt">
                  {a.estadoNaAula ? (
                    <Status situacao={SITUACAO_DO_ESTADO[a.estadoNaAula]}>
                      {ROTULO_ESTADO_NA_CHAMADA[a.estadoNaAula]}
                    </Status>
                  ) : (
                    <small>sem chamada</small>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
